/**
 * InboxBell — the inbox BUTTON in the HEADER ROW (Ori's placement: beside the
 * menu button, the LEFT cluster). THE INBOX IS ITS OWN LEFT-RAIL OVERLAY
 * (the confirm-loop wave, Ori 2026-07-25): sci//feed:@binary:@inbox XOR
 * sci//feed:@binary:@tab** — the bell opens the LIVE inbox overlay; opening
 * it closes the tab strip (one word, one writer world-side).
 *
 * The button renders the served inbox icon word (sci//feed:@inbox:@(ui:kind:icon))
 * + the unseen badge, and toggles the INBOX overlay lane through the feed
 * rail write lane.
 *
 * THE BADGE RULE (Ori, the confirm-loop wave): the counter counts ONLY
 * unseen NOTIFY entries — status and sync accrete into the list (they are
 * THERE) but never count and never ring. Opening writes ONE SEEN claim per
 * unseen NOTIFY entry (the exact set the badge showed) and folds the same
 * claims locally (keyed, idempotent world-side); status/sync entries stay
 * unseen until their own row is clicked in the panel.
 */
import React, { useEffect } from "react";
import { Bell } from "lucide-react";

import { feedStore, useFeedStoreState } from "./feedStore";
import {
  feedRailAnnounce,
  inboxSeenAnnounce,
  lucideIconOfWord,
  stableInboxRefOf,
  unseenInboxEntries,
} from "./feedWave";
import type { ChannelApi } from "./useChannel";

export const InboxBell: React.FC<{ ch: ChannelApi }> = ({ ch }) => {
  const feed = useFeedStoreState();
  const channelQuery = ch.query;
  const userId = ch.userId;
  const channelReady = ch.ready;

  useEffect(() => {
    // never before auth — a token-less ask is a guaranteed 401 (the zero-401
    // browser law); ensureArrangement is SINGLE-FLIGHT — the bell, the rail,
    // and the chrome share the ONE `query {tab:"feed"}`.
    if (!channelReady) return;
    void feedStore.ensureArrangement(channelQuery);
  }, [channelQuery, channelReady]);

  const open = feed.feed?.left === "inbox";
  // THE BADGE RULE (the confirm-loop wave): ONLY unseen NOTIFY entries count
  // — status/sync are in the list but never ring; the open sweep seen-writes
  // exactly the counted set.
  const unseenCounted = unseenInboxEntries(feed.inbox?.list ?? [], feed.inbox?.seen ?? [])
    .filter((entry) => entry.kind === "notify");
  const unseen = unseenCounted.length;

  const toggle = () => {
    // THE CLICK DECLARES (Ori 2026-08-04): the store resolves the absolute next
    // value off its LIVE projection and folds it in ONE step — deliver first,
    // never wait, and never re-derive `next` from a render snapshot that a
    // racing serve can have already replaced (that inverted the click).
    const next = feedStore.declareRail("inbox");
    if (!userId) return;
    const writes: Array<Promise<unknown>> = [ch.announce(feedRailAnnounce(userId, "inbox", next))];
    const stamp = new Date().toISOString().replace(/Z$/, "");
    const seenRefs: string[] = [];
    if (next) {
      for (const entry of unseenCounted) {
        const ref = stableInboxRefOf(entry);
        seenRefs.push(ref);
        writes.push(ch.announce(inboxSeenAnnounce(userId, ref, stamp)));
      }
      feedStore.markLocalWorkSeen();
    }
    void Promise.all(writes)
      .then(() => {
        // fold the SAME keyed seen claims locally (idempotent world-side);
        // the next generation's reconcile trues them up — no refetch.
        if (seenRefs.length) feedStore.applyInboxSeen(seenRefs, stamp);
      })
      .catch((error) => console.error("[inbox] the write failed (never reached the world):", error));
  };

  const InboxIcon = lucideIconOfWord(feed.feed?.icon.inbox ?? "") ?? Bell;
  return (
    <button
      type="button"
      onClick={toggle}
      data-inbox-bell
      data-open={open ? "true" : "false"}
      aria-label={unseen ? `Inbox — ${unseen} unseen` : "Inbox"}
      aria-expanded={open}
      title="Inbox"
      style={{
        position: "relative", display: "grid", placeItems: "center",
        width: 32, height: 32, borderRadius: 8, border: "none", cursor: "pointer",
        background: open ? "var(--color-surface)" : "transparent",
        color: "var(--color-text-primary)",
      }}
    >
      <InboxIcon size={18} />
      {unseen > 0 && (
        <span
          data-inbox-badge
          style={{
            position: "absolute", top: 2, right: 2, minWidth: 14, height: 14,
            borderRadius: 7, padding: "0 3px", fontSize: 9, fontWeight: 700,
            lineHeight: "14px", textAlign: "center",
            background: "var(--accent-primary, #6B7EEA)", color: "#fff",
          }}
        >
          {unseen}
        </span>
      )}
    </button>
  );
};
